import { Button, Dialog, useTestTag } from '@myosh/odin-components';
import type { Ref } from 'react';
import { forwardRef, useCallback, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ExtendedDynamicFormNextWorkflowStep } from '../../../@types/forms';

interface WorkflowStepModalState {
  nextStep?: ExtendedDynamicFormNextWorkflowStep;
  visible: boolean;
}

const initialState: WorkflowStepModalState = {
  nextStep: undefined,
  visible: false,
};

export interface WorkflowStepModalRef {
  show: (nextStepLabel: ExtendedDynamicFormNextWorkflowStep) => Promise<ExtendedDynamicFormNextWorkflowStep>;
}

const WorkflowStepModal = (_: unknown, ref: Ref<WorkflowStepModalRef>) => {
  const [state, setState] = useState<WorkflowStepModalState>(initialState);
  const promiseResolveRef = useRef<
    | ((value: ExtendedDynamicFormNextWorkflowStep | PromiseLike<ExtendedDynamicFormNextWorkflowStep>) => void)
    | undefined
  >(undefined);
  const { t } = useTranslation();

  const tagCreator = useTestTag('workflowstep-modal');

  const showDialog = useCallback((nextStep: ExtendedDynamicFormNextWorkflowStep) => {
    setState(() => {
      return {
        nextStep,
        visible: true,
      };
    });
  }, []);

  const hideDialog = useCallback(() => {
    setState((state) => {
      return {
        ...state,
        visible: false,
      };
    });
  }, []);

  const handleClose = useCallback(() => {
    promiseResolveRef.current?.({} as ExtendedDynamicFormNextWorkflowStep);
    setState(initialState);
  }, []);

  const show = (nextStep: ExtendedDynamicFormNextWorkflowStep) => {
    showDialog(nextStep);

    return new Promise<ExtendedDynamicFormNextWorkflowStep>((resolve) => {
      promiseResolveRef.current = resolve;
    });
  };

  useImperativeHandle(ref, () => ({
    show,
  }));

  const handleProgressToNextStep = () => {
    if (state.nextStep) {
      promiseResolveRef.current?.(state.nextStep);
    }
    hideDialog();
  };

  const footer = (
    <div className="flex justify-end" ref={tagCreator('buttons')}>
      <Button type="primary" onClick={handleProgressToNextStep}>
        {state?.nextStep?.buttonCaption}
      </Button>
    </div>
  );

  return (
    <>
      {state.visible && (
        <Dialog header={t(`record-will-change-status`)} onClose={handleClose} visible={state.visible} footer={footer}>
          <h1>{String(t(`continue-message`))}</h1>
        </Dialog>
      )}
    </>
  );
};

export default forwardRef(WorkflowStepModal);
